import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import { Labels } from "../../util/Translations"

import { IAIModals, IAIModal, IMessage, IInputButton, IId } from "./index"

export interface Initialize extends IId {
    inputButtons: IInputButton[]
    messages: IMessage[]
}

export interface IAddMessage extends IId {
    message: IMessage
}

export interface IAdjustBoolean extends IId {
    value: boolean
}

export const AIModalSlice = createSlice({
    name: "AIModal",

    initialState: {
        modals: {},
    } as IAIModals,

    reducers: {
        initialize: (state, action: PayloadAction<Initialize>) => {
            const { id, inputButtons, messages } = action.payload

            if (!state.modals[id]) {
                const baseModal: IAIModal = {
                    isLoading: false,
                    isVisible: false,
                    messages: [],
                    inputButtons,
                }

                state.modals[id] = baseModal

                // we start with a disclaimer message
                addNewMessage(state, id, {
                    type: "HTML",
                    content: Labels.AIDisclaimer,
                    isSentByUser: false,
                    id: `${id}-disclaimer`,
                })

                // initialize other starting messages
                messages.forEach((message) => {
                    addNewMessage(state, id, {
                        type: message.type,
                        content: message.content,
                        isSentByUser: message.isSentByUser,
                        id: message.id,
                    })
                })
            }
            // if we reload, we want to keep all the data
            // and due to the fact that we can cancel ajax calls like that
            // we want to ensure the loading animation is cancelled
            // otherwise the AI modal will not be interactable
            else {
                state.modals[id].isLoading = false
            }
        },
        setIsLoading: (state, action: PayloadAction<IAdjustBoolean>) => {
            state.modals[action.payload.id].isLoading = action.payload.value
        },
        setIsVisible: (state, action: PayloadAction<IAdjustBoolean>) => {
            state.modals[action.payload.id].isVisible = action.payload.value
        },
        reset: (state, action: PayloadAction<string>) => {
            state.modals[action.payload].messages = []
        },
        addMessage: (state, action: PayloadAction<IAddMessage>) => {
            const { id, message } = action.payload
            addNewMessage(state, id, message)
        },
    },
})

const addNewMessage = (
    state: IAIModals,
    id: string,
    message: IMessage,
): void => {
    state.modals[id].messages.push({
        ...message,
        timestamp: new Date().toISOString(),
    })
}

export default AIModalSlice.reducer

export const { addMessage, initialize, reset, setIsLoading, setIsVisible } =
    AIModalSlice.actions
